/**
 * View: Detalhe completo de uma proposta.
 *
 * Mostra dados de forma legível (não JSON) e oferece ações:
 *  - Mudar status (com motivo opcional) — só transições válidas
 *  - Atalhos de status frequentes (Enviar, Aceitar, Cancelar, Perdida...)
 *  - Ver payload bruto (debug, em <details>)
 *
 * Edição inline de campos fica pro 2.4.1.
 */

import {
  nextStatuses,
  rotuloOnboarding,
  type LinhaBreakdown,
  type PricingSnapshot,
  type Proposta,
  type StatusProposta,
} from '@infomatic/shared';

import { api, ApiError } from '../../lib/api.js';
import { fmtDateTime } from '../../lib/formatters.js';
import { fmtBRL } from '../../lib/pricing.js';
import { escapeHtml, renderLayout } from '../layout.js';
import { getRouter } from '../router.js';

import { badgeClass, formatStatus } from './status-helpers.js';

interface DetalheState {
  proposta: Proposta | null;
  loading: boolean;
  acting: boolean;
  error: string | null;
}

const state: DetalheState = {
  proposta: null,
  loading: false,
  acting: false,
  error: null,
};

export async function renderDetalheProposta(id: string): Promise<void> {
  const $app = document.getElementById('app');
  if (!$app) return;

  $app.innerHTML = renderLayout({
    title: 'Proposta',
    activeNav: 'propostas',
    bodyHtml: `
      <div style="margin-bottom: var(--sp-4)">
        <a href="/propostas" data-route style="color: var(--color-text-secondary); text-decoration: none">
          ← Voltar à lista
        </a>
      </div>
      <section class="card" id="detalhe-card">
        <div>Carregando proposta…</div>
      </section>
    `,
  });

  injectDetalheStyles();
  await fetchAndRender(id);
}

async function fetchAndRender(id: string): Promise<void> {
  state.loading = true;
  state.error = null;
  try {
    const { proposta } = await api.propostas.get(id);
    state.proposta = proposta;
  } catch (err) {
    state.proposta = null;
    state.error = err instanceof Error ? err.message : 'Erro ao carregar';
  } finally {
    state.loading = false;
    rerender();
  }
}

function rerender(): void {
  const $card = document.getElementById('detalhe-card');
  if (!$card) return;

  if (state.loading) {
    $card.innerHTML = '<div>Carregando proposta…</div>';
    return;
  }

  if (state.error || !state.proposta) {
    $card.innerHTML = `<div class="badge badge-error">${escapeHtml(state.error ?? 'Proposta não encontrada')}</div>`;
    return;
  }

  $card.innerHTML = renderConteudo(state.proposta);
  attachAcoes();
  void carregarTimeline(state.proposta.id);
}

function renderConteudo(p: Proposta): string {
  const proximos = nextStatuses(p.status);
  const isFinal = proximos.length === 0;

  const acoes = isFinal
    ? '<p style="color: var(--color-text-muted)">Proposta em status final — sem ações disponíveis.</p>'
    : `
      <div class="acoes-status">
        <label class="form-field">
          <span>Mudar para</span>
          <select id="novo-status">
            ${proximos.map((s) => `<option value="${escapeHtml(s)}">${escapeHtml(formatStatus(s))}</option>`).join('')}
          </select>
        </label>
        <label class="form-field">
          <span id="motivo-label">Motivo (opcional)</span>
          <input type="text" id="motivo" placeholder="Por que está mudando o status?" maxlength="200" />
        </label>
        <button type="button" class="btn btn-primary" data-acao="mudar-status">Confirmar mudança</button>
      </div>
    `;

  const cliente = p.cliente;
  const docFormatado = formatDoc(cliente);
  const dispositivos = p.tipo === 'contrato-recorrente' ? p.payload.recursos.dispositivos : null;
  const usuarios = p.tipo === 'contrato-recorrente' ? p.payload.recursos.usuarios : null;
  const pacote = p.tipo === 'contrato-recorrente' ? p.payload.pacotePrincipal : null;
  const onboarding =
    p.tipo === 'contrato-recorrente' ? rotuloOnboarding(p.payload.onboarding) : null;
  const fidelidade = p.tipo === 'contrato-recorrente' ? p.payload.prazoFidelidade : null;

  return `
    <div class="detalhe-header">
      <div>
        <h1 class="detalhe-titulo">${escapeHtml(p.numero)}</h1>
        <div class="detalhe-subtitulo">${escapeHtml(cliente.empresa)} · ${escapeHtml(p.tipo)}</div>
      </div>
      <div style="display:flex;gap:var(--sp-2);align-items:center">
        ${
          p.status === 'rascunho' && p.tipo === 'contrato-recorrente'
            ? `<a href="/propostas/${encodeURIComponent(p.id)}/editar" data-route class="btn btn-secondary">Editar</a>`
            : ''
        }
        ${
          p.tipo === 'contrato-recorrente'
            ? `<button type="button" class="btn btn-secondary" data-acao="clonar">Clonar</button>`
            : ''
        }
        <button type="button" class="btn btn-secondary" data-acao="previa" title="Vê a proposta como o cliente, sem enviar nem mudar o status">Prévia</button>
        ${
          p.status !== 'rascunho'
            ? `<button type="button" class="btn btn-secondary" data-acao="abrir-link-publico" title="Gera token e abre a proposta como o cliente veria">Abrir link público</button>`
            : ''
        }
        ${
          p.tipo === 'contrato-recorrente'
            ? `<button type="button" class="btn btn-secondary" data-acao="baixar-contrato" title="${p.dadosContrato ? 'Baixar contrato preenchido' : 'Aguardando cliente preencher dados do contrato'}">Baixar contrato</button>`
            : ''
        }
        ${
          p.tipo === 'contrato-recorrente' &&
          p.dadosContrato &&
          (!p.assinatura || p.assinatura.status === 'cancelado')
            ? `<button type="button" class="btn btn-primary" data-acao="enviar-clicksign" title="Envia contrato pro Clicksign (consome créditos)">Enviar pro Clicksign</button>`
            : ''
        }
        ${
          p.tipo === 'contrato-recorrente' &&
          p.dadosContrato &&
          (!p.assinatura || p.assinatura.status === 'cancelado')
            ? `<button type="button" class="btn btn-secondary" data-acao="vincular-clicksign" title="Vincular documento já criado no Clicksign (recuperação)">Vincular documento existente</button>`
            : ''
        }
        ${
          p.tipo === 'contrato-recorrente' && p.assinatura && p.assinatura.status !== 'completo'
            ? `<button type="button" class="btn btn-secondary" data-acao="atualizar-assinatura" title="Consulta status atual no Clicksign">Atualizar status</button>`
            : ''
        }
        ${
          p.assinatura &&
          (p.assinatura.status === 'aguardando' || p.assinatura.status === 'parcialmente-assinado')
            ? `<button type="button" class="btn btn-secondary" data-acao="reenviar-assinatura" title="Reenvia o e-mail de assinatura pro cliente (mesmo contrato, sem gerar novo)">Reenviar contrato</button>`
            : ''
        }
        ${
          p.status === 'implantacao'
            ? `<button type="button" class="btn btn-secondary" data-acao="reenviar-implantacao" title="Reenvia o e-mail pro gestor de implantação">Reenviar e-mail gestor</button>`
            : ''
        }
        <span class="badge badge-status badge-${badgeClass(p.status)}">${escapeHtml(formatStatus(p.status))}</span>
      </div>
    </div>

    <div class="detalhe-timeline" id="detalhe-timeline">
      <div class="timeline-loading">Carregando timeline…</div>
    </div>

    <div class="detalhe-grid">
      <section>
        <h3 class="detalhe-h3">Cliente</h3>
        <dl class="detalhe-dl">
          <dt>Empresa</dt><dd>${escapeHtml(cliente.empresa)}</dd>
          ${docFormatado ? `<dt>Documento</dt><dd>${escapeHtml(docFormatado)}</dd>` : ''}
          <dt>Responsável</dt><dd>${escapeHtml(cliente.responsavel)}</dd>
          <dt>E-mail</dt><dd>${escapeHtml(cliente.email)}</dd>
          ${cliente.telefone ? `<dt>Telefone</dt><dd>${escapeHtml(formatTelefone(cliente.telefone))}</dd>` : ''}
        </dl>
      </section>

      <section>
        <h3 class="detalhe-h3">Proposta</h3>
        <dl class="detalhe-dl">
          <dt>Vendedor</dt><dd>${escapeHtml(p.vendedor.nome)} <small style="color: var(--color-text-muted)">(${escapeHtml(p.vendedor.email)})</small></dd>
          <dt>Validade</dt><dd>${escapeHtml(p.validade)}</dd>
          <dt>Criada em</dt><dd>${fmtDateTime(p.dataCriacao)}</dd>
          <dt>Atualizada em</dt><dd>${fmtDateTime(p.dataAtualizacao)}</dd>
        </dl>
      </section>

      ${
        p.tipo === 'contrato-recorrente'
          ? `
        <section>
          <h3 class="detalhe-h3">Contrato</h3>
          <dl class="detalhe-dl">
            ${pacote ? `<dt>Pacote</dt><dd>${escapeHtml(pacote)}</dd>` : ''}
            ${onboarding ? `<dt>Onboarding</dt><dd>${escapeHtml(onboarding)}</dd>` : ''}
            ${fidelidade ? `<dt>Fidelidade</dt><dd>${escapeHtml(fidelidade)}</dd>` : ''}
            ${dispositivos !== null ? `<dt>Estações</dt><dd>${dispositivos}</dd>` : ''}
            ${usuarios !== null ? `<dt>Usuários M365</dt><dd>${usuarios}</dd>` : ''}
          </dl>
        </section>
      `
          : ''
      }

      ${p.contatosContrato ? renderContatosContrato(p.contatosContrato) : ''}
      ${p.dadosContrato ? renderDadosContratoAdmin(p.dadosContrato) : ''}
      ${p.observacoes ? renderObservacoesEstruturadas(p.observacoes) : ''}
      ${p.observacoesInternas ? renderObservacoesInternas(p.observacoesInternas) : ''}
    </div>

    ${p.tipo === 'contrato-recorrente' && p.pricingSnapshot ? renderResumoInvestimento(p.pricingSnapshot) : ''}

    <div class="detalhe-acoes">
      <h3 class="detalhe-h3">Ações</h3>
      ${acoes}
      <div id="acao-feedback" style="margin-top: var(--sp-3); display: none"></div>
    </div>

    ${
      ['rascunho', 'enviada', 'visualizada'].includes(p.status)
        ? `
      <div class="detalhe-acoes">
        <h3 class="detalhe-h3">Enviar proposta por e-mail</h3>
        <p style="color: var(--color-text-secondary); margin-bottom: var(--sp-3)">
          Manda direto pro e-mail do cliente (${escapeHtml(p.cliente.email)}) com o link já embutido. O e-mail responde pra você como vendedor.
        </p>
        <div class="link-publico-box">
          <button type="button" class="btn btn-primary" data-acao="enviar-email">${p.status === 'rascunho' ? 'Enviar para cliente' : 'Reenviar para cliente'}</button>
          <span id="enviar-feedback" style="color: var(--color-text-muted); flex: 1"></span>
        </div>
        ${
          p.envios && p.envios.length > 0
            ? `<details style="margin-top: var(--sp-3)"><summary style="cursor: pointer; color: var(--color-text-secondary); font-size: var(--fs-sm)">Histórico de envios (${p.envios.length})</summary>
                <ul style="margin: var(--sp-2) 0 0; padding-left: var(--sp-5); font-size: var(--fs-sm); color: var(--color-text-secondary)">
                ${p.envios.map((e) => `<li>${fmtDateTime(e.ts)} — ${escapeHtml(e.canal)} → ${escapeHtml(e.destino)} <em>(${escapeHtml(e.status)})</em></li>`).join('')}
                </ul></details>`
            : ''
        }
      </div>
    `
        : ''
    }

    ${
      p.status !== 'rascunho'
        ? `
      <div class="detalhe-acoes">
        <h3 class="detalhe-h3">Link público pro cliente</h3>
        <p style="color: var(--color-text-secondary); margin-bottom: var(--sp-3)">
          Gere um link único que o cliente pode acessar sem login.
        </p>
        <div class="link-publico-box">
          <button type="button" class="btn btn-secondary" data-acao="gerar-link">Gerar e copiar link</button>
          <span id="link-feedback" style="color: var(--color-text-muted); flex: 1"></span>
        </div>
      </div>
    `
        : ''
    }

    <details style="margin-top: var(--sp-6)">
      <summary style="cursor: pointer; color: var(--color-text-secondary)">Ver payload bruto (JSON)</summary>
      <pre class="detalhe-json">${escapeHtml(JSON.stringify(p, null, 2))}</pre>
    </details>
  `;
}

function attachAcoes(): void {
  // Atualiza label/placeholder do motivo conforme status selecionado
  const $select = document.getElementById('novo-status') as HTMLSelectElement | null;
  const $motivo = document.getElementById('motivo') as HTMLInputElement | null;
  const $motivoLabel = document.getElementById('motivo-label');
  function syncMotivoUi(): void {
    if (!$select || !$motivo || !$motivoLabel) return;
    if ($select.value === 'perdida') {
      $motivoLabel.textContent = 'Motivo da perda *';
      $motivo.placeholder = 'Ex: preço, concorrente, prazo, sem orçamento...';
      $motivo.required = true;
    } else if ($select.value === 'cancelada') {
      $motivoLabel.textContent = 'Motivo do cancelamento (opcional)';
      $motivo.placeholder = 'Por que está cancelando?';
      $motivo.required = false;
    } else {
      $motivoLabel.textContent = 'Motivo (opcional)';
      $motivo.placeholder = 'Por que está mudando o status?';
      $motivo.required = false;
    }
  }
  $select?.addEventListener('change', syncMotivoUi);
  syncMotivoUi();

  document.querySelectorAll<HTMLElement>('[data-acao]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const acao = btn.dataset.acao;
      if (acao === 'mudar-status') {
        void onMudarStatus();
      } else if (acao === 'gerar-link') {
        void onGerarLink();
      } else if (acao === 'enviar-email') {
        void onEnviarEmail();
      } else if (acao === 'clonar') {
        void onClonar(btn as HTMLButtonElement);
      } else if (acao === 'baixar-contrato') {
        void onBaixarContrato(btn as HTMLButtonElement);
      } else if (acao === 'previa') {
        void onPrevia(btn as HTMLButtonElement);
      } else if (acao === 'abrir-link-publico') {
        void onAbrirLinkPublico(btn as HTMLButtonElement);
      } else if (acao === 'enviar-clicksign') {
        void onEnviarClicksign(btn as HTMLButtonElement);
      } else if (acao === 'atualizar-assinatura') {
        void onAtualizarAssinatura(btn as HTMLButtonElement);
      } else if (acao === 'vincular-clicksign') {
        void onVincularClicksign(btn as HTMLButtonElement);
      } else if (acao === 'reenviar-assinatura') {
        void onReenviarAssinatura(btn as HTMLButtonElement);
      } else if (acao === 'reenviar-implantacao') {
        void onReenviarImplantacao(btn as HTMLButtonElement);
      }
    });
  });
}

/**
 * Recupera proposta cujo documento já foi criado no Clicksign mas não foi
 * salvo localmente (bug, timeout). Admin cola o documentKey do email/painel.
 */
async function onVincularClicksign(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  const documentKey = prompt(
    `Cole o documentKey do Clicksign\n\n` +
      `Como pegar: no painel Clicksign, abra o documento e copie o ID da URL ` +
      `(parte depois de /documents/). Exemplo: 877b950c-3621-46bf-a23a-f4a69c1d591c`,
  );
  if (!documentKey || documentKey.trim().length < 10) return;

  const labelOriginal = btn.textContent ?? 'Vincular documento existente';
  btn.disabled = true;
  btn.textContent = 'Vinculando...';
  try {
    const resp = await api.propostas.vincularClicksign(p.id, documentKey.trim());
    alert(
      `Documento vinculado.\n\n` +
        `Status assinatura: ${resp.assinatura.status}\n` +
        `Status proposta: ${resp.proposta.status}`,
    );
    await renderDetalheProposta(p.id);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Falha ao vincular: ${msg}`);
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Reenvia o e-mail de assinatura pro cliente (mesmo envelope, sem gerar contrato novo).
 */
async function onReenviarAssinatura(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  if (!confirm(`Reenviar o e-mail de assinatura do contrato ${p.numero} pro cliente?`)) return;

  const labelOriginal = btn.textContent ?? 'Reenviar contrato';
  btn.disabled = true;
  btn.textContent = 'Reenviando...';
  try {
    await api.propostas.reenviarAssinatura(p.id);
    alert('E-mail de assinatura reenviado pro cliente.');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Falha ao reenviar: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Reenvia o e-mail pro gestor de implantação (proposta já em implantação).
 */
async function onReenviarImplantacao(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  if (!confirm(`Reenviar o e-mail pro gestor de implantação da proposta ${p.numero}?`)) return;

  const labelOriginal = btn.textContent ?? 'Reenviar e-mail gestor';
  btn.disabled = true;
  btn.textContent = 'Reenviando...';
  try {
    await api.propostas.reenviarEmailImplantacao(p.id);
    alert('E-mail do gestor de implantação reenviado.');
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Falha ao reenviar: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Envia contrato pro Clicksign. Confirma antes — produção COBRA CRÉDITOS por documento.
 */
async function onEnviarClicksign(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  if (!p.dadosContrato) {
    alert('Cliente precisa preencher dados do contrato antes do envio.');
    return;
  }
  const responsavel = p.dadosContrato.responsavelLegal;
  const confirma = confirm(
    `Enviar contrato ${p.numero} pro Clicksign?\n\n` +
      `Signatário: ${responsavel.nome} (${responsavel.email})\n` +
      `CPF: ${responsavel.cpf}\n\n` +
      `ATENÇÃO: produção Clicksign CONSOME CRÉDITOS por documento criado.`,
  );
  if (!confirma) return;

  const labelOriginal = btn.textContent ?? 'Enviar pro Clicksign';
  btn.disabled = true;
  btn.textContent = 'Enviando...';

  try {
    const resp = await api.propostas.enviarClicksign(p.id);
    alert(
      `Contrato enviado pro Clicksign.\n\n` +
        `Documento: ${resp.assinatura.documentoId}\n` +
        `Status: ${resp.assinatura.status}\n\n` +
        `O signatário receberá o link de assinatura por e-mail.`,
    );
    // Recarrega a tela pra refletir nova assinatura
    await renderDetalheProposta(p.id);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Falha ao enviar pro Clicksign: ${msg}`);
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Polling manual no Clicksign — atualiza status da assinatura na proposta.
 */
async function onAtualizarAssinatura(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  const labelOriginal = btn.textContent ?? 'Atualizar status';
  btn.disabled = true;
  btn.textContent = 'Consultando...';

  try {
    const resp = await api.propostas.atualizarAssinatura(p.id);
    if (resp.mudou) {
      const detalhe = resp.assinatura.assinantes
        .map((a) => `${a.nome}: ${a.status}${a.assinadoEm ? ` (em ${a.assinadoEm})` : ''}`)
        .join('\n');
      alert(`Status atualizado: ${resp.assinatura.status}\n\n${detalhe}`);
      await renderDetalheProposta(p.id);
    } else {
      alert(`Sem mudanças desde a última consulta. Status atual: ${resp.assinatura.status}`);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Falha ao consultar Clicksign: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Gera o link público da proposta (POST /link) e abre em nova aba.
 * Evita copy/paste manual do token pelo admin.
 *
 * Truque pra evitar bloqueio de popup: window.open depois de await perde
 * o contexto do user-click e o browser bloqueia. Usamos `<a target=_blank>`
 * simulado, que é interpretado como navegação iniciada pelo usuário.
 */
/**
 * Prévia — abre a proposta como o cliente vê, SEM enviar nem mudar status.
 * Usa token de prévia (claim preview): não marca visualizada, não notifica,
 * não loga "enviada". Funciona em qualquer status, inclusive rascunho.
 */
async function onPrevia(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  const labelOriginal = btn.textContent ?? 'Prévia';
  btn.disabled = true;
  btn.textContent = 'Gerando prévia...';
  try {
    // preview=true; usa a URL longa /p/<token> (prévia não persiste short-link).
    const resp = await api.propostas.gerarLink(p.id, window.location.origin, true);
    const a = document.createElement('a');
    a.href = resp.url;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Erro ao gerar prévia: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

async function onAbrirLinkPublico(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  const labelOriginal = btn.textContent ?? 'Abrir link público';
  btn.disabled = true;
  btn.textContent = 'Gerando link...';
  try {
    const baseUrl = window.location.origin;
    const resp = await api.propostas.gerarLink(p.id, baseUrl);
    // #259 — Prefere urlCurta (~50 chars, sobrevive ao WhatsApp/SMS).
    // Fallback pra url longa se backend antigo não retornar a curta.
    const link = (resp as unknown as { urlCurta?: string }).urlCurta ?? resp.url;
    // Truque: <a> com target=_blank passa pelo bloqueador de popup.
    const a = document.createElement('a');
    a.href = link;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Erro ao gerar link público: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Baixa o .docx do contrato preenchido (gerado server-side via docxtemplater).
 * Requer que dadosContrato esteja preenchido pelo cliente no fluxo pós-aceite.
 */
async function onBaixarContrato(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  if (!p.dadosContrato) {
    alert('Aguardando o cliente preencher os dados do contrato.');
    return;
  }

  const labelOriginal = btn.textContent ?? 'Baixar contrato';
  btn.disabled = true;
  btn.textContent = 'Gerando...';

  try {
    const blob = await api.propostas.baixarContrato(p.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato-${p.numero}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erro desconhecido';
    alert(`Erro ao baixar contrato: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

/**
 * Clona a proposta atual: cria uma nova com mesmos dados e status `rascunho`.
 * Vai direto pra tela de edição da cópia pro vendedor ajustar antes de enviar.
 */
async function onClonar(btn: HTMLButtonElement): Promise<void> {
  if (!state.proposta) return;
  const p = state.proposta;
  if (p.tipo !== 'contrato-recorrente') {
    alert('Clonagem só está disponível pra propostas de contrato recorrente neste momento.');
    return;
  }

  const labelOriginal = btn.textContent ?? 'Clonar';
  btn.disabled = true;
  btn.textContent = 'Clonando...';

  try {
    // Clonagem completa: copia TODOS os campos editáveis do contrato original.
    // O backend re-gera id/numero/dataCriacao/status/pricingSnapshot.
    const input = {
      tipo: 'contrato-recorrente' as const,
      cliente: p.cliente,
      vendedor: p.vendedor,
      validade: p.validade,
      payload: p.payload,
      observacoes: p.observacoes,
      // Config de exibição (modoAddOns, mostrarValoresUnitarios, etc) —
      // estava faltando, então o clone vinha sempre em modo "compacto" default.
      exibicao: p.exibicao,
    };
    const { proposta: nova } = await api.propostas.create(input);
    // Vai direto pra edição da cópia
    getRouter().navigate(`/propostas/${encodeURIComponent(nova.id)}/editar`);
  } catch (err) {
    const msg =
      err instanceof ApiError ? `${err.code ?? 'ERRO'}: ${err.message}` : (err as Error).message;
    alert(`Falha ao clonar: ${msg}`);
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

async function onGerarLink(): Promise<void> {
  if (!state.proposta) return;
  const $fb = document.getElementById('link-feedback');
  if (!$fb) return;
  $fb.textContent = 'Gerando link…';
  $fb.style.color = 'var(--color-text-muted)';

  try {
    const resp = await api.propostas.gerarLink(state.proposta.id);
    // Prefere urlCurta (~50 chars, sobrevive a WhatsApp/SMS). Cai pra url longa
    // se backend antigo não retornar a curta ainda.
    const link = (resp as unknown as { urlCurta?: string }).urlCurta ?? resp.url;
    try {
      await navigator.clipboard.writeText(link);
      $fb.textContent = `Copiado: ${link}`;
      $fb.style.color = 'var(--color-success, #0a0)';
    } catch {
      // Clipboard pode falhar em alguns browsers — mostra o link inline pro user copiar
      $fb.innerHTML = `<a href="${escapeHtml(link)}" target="_blank" rel="noopener">${escapeHtml(link)}</a>`;
      $fb.style.color = 'var(--color-text)';
    }
  } catch (err) {
    const msg =
      err instanceof ApiError ? `${err.code ?? 'ERRO'}: ${err.message}` : (err as Error).message;
    $fb.textContent = msg;
    $fb.style.color = 'var(--color-error, #c00)';
  }
}

async function onEnviarEmail(): Promise<void> {
  if (!state.proposta) return;
  const $btn = document.querySelector<HTMLButtonElement>('[data-acao="enviar-email"]');
  const $fb = document.getElementById('enviar-feedback');
  if (!$btn || !$fb) return;

  const confirmacao = confirm(
    `Enviar a proposta para ${state.proposta.cliente.email}?\n\nO cliente recebe um e-mail com o link da proposta.`,
  );
  if (!confirmacao) return;

  $btn.disabled = true;
  $btn.textContent = 'Enviando...';
  $fb.textContent = '';

  try {
    const resp = await api.propostas.enviar(state.proposta.id);
    state.proposta = resp.proposta;
    $fb.style.color = resp.emailOk ? 'var(--color-success)' : 'var(--color-warning)';
    $fb.textContent = resp.emailOk
      ? `E-mail enviado pra ${state.proposta.cliente.email}.`
      : `Status atualizado, mas envio falhou: ${resp.emailReason ?? 'erro'}`;
    rerender();
  } catch (err) {
    $btn.disabled = false;
    $btn.textContent =
      state.proposta?.status === 'rascunho' ? 'Enviar para cliente' : 'Reenviar para cliente';
    const msg =
      err instanceof ApiError ? `${err.code ?? 'ERRO'}: ${err.message}` : (err as Error).message;
    $fb.style.color = 'var(--color-danger)';
    $fb.textContent = msg;
  }
}

async function onMudarStatus(): Promise<void> {
  if (state.acting || !state.proposta) return;
  const $select = document.getElementById('novo-status') as HTMLSelectElement | null;
  const $motivo = document.getElementById('motivo') as HTMLInputElement | null;
  if (!$select) return;

  const novoStatus = $select.value as StatusProposta;
  const motivo = $motivo?.value.trim() ?? '';

  if (novoStatus === 'perdida' && motivo.length < 3) {
    setFeedback('Motivo obrigatório (mínimo 3 caracteres) ao marcar como perdida.', 'error');
    $motivo?.focus();
    return;
  }

  state.acting = true;
  setFeedback('Aplicando…', 'info');

  try {
    const { proposta } = await api.propostas.mudarStatus(
      state.proposta.id,
      novoStatus,
      motivo || undefined,
    );
    state.proposta = proposta;
    setFeedback(`Status alterado para "${formatStatus(novoStatus)}".`, 'success');
    rerender();
  } catch (err) {
    const msg =
      err instanceof ApiError ? `${err.code ?? 'ERRO'}: ${err.message}` : (err as Error).message;
    setFeedback(msg, 'error');
  } finally {
    state.acting = false;
  }
}

function setFeedback(msg: string, kind: 'info' | 'success' | 'error'): void {
  const $f = document.getElementById('acao-feedback');
  if (!$f) return;
  $f.textContent = msg;
  $f.style.display = 'block';
  $f.className = `badge badge-${kind === 'error' ? 'error' : kind === 'success' ? 'ok' : 'info'}`;
}

async function carregarTimeline(propostaId: string): Promise<void> {
  const $el = document.getElementById('detalhe-timeline');
  if (!$el) return;
  try {
    const tl = await api.propostas.timeline(propostaId);
    $el.innerHTML = renderTimeline(tl);
  } catch {
    $el.innerHTML = '';
  }
}

interface TimelineDTO {
  etapas: { key: string; label: string; completadaEm: string | null }[];
  terminal: { tipo: 'perdida' | 'cancelada'; label: string; em: string; motivo?: string } | null;
}

function renderTimeline(tl: TimelineDTO): string {
  const etapas = tl.etapas;
  const lastCompletedIdx = etapas.reduce((acc, e, i) => (e.completadaEm ? i : acc), -1);

  const stepsHtml = etapas
    .map((e, i) => {
      const completa = Boolean(e.completadaEm);
      const atual = !completa && i === lastCompletedIdx + 1 && !tl.terminal;
      const cls = completa ? 'completa' : atual ? 'atual' : 'futura';
      const data = e.completadaEm ? fmtDateTime(e.completadaEm) : '';
      return `
        <li class="tl-step tl-${cls}">
          <div class="tl-dot">${completa ? '✓' : atual ? '●' : ''}</div>
          <div class="tl-label">${escapeHtml(e.label)}</div>
          <div class="tl-data">${escapeHtml(data)}</div>
        </li>
      `;
    })
    .join('');

  const terminalHtml = tl.terminal
    ? `<div class="tl-terminal tl-terminal-${tl.terminal.tipo}">
        <strong>${escapeHtml(tl.terminal.label)}</strong>
        em ${fmtDateTime(tl.terminal.em)}
        ${tl.terminal.motivo ? ` — ${escapeHtml(tl.terminal.motivo)}` : ''}
      </div>`
    : '';

  return `
    <ol class="tl-list">${stepsHtml}</ol>
    ${terminalHtml}
  `;
}

// ── Formatters ────────────────────────────────────────────────────

/**
 * Renderiza o resumo de investimento (breakdown completo do pricing) no
 * detalhe admin. Usa o pricingSnapshot da proposta — mesma estrutura do
 * painel lateral do form de nova proposta, sem o card escuro grande da
 * view pública (esse é só pro cliente final).
 */
/**
 * Renderiza o cronograma de desconto promocional escalonado no resumo admin.
 * Mostra mês-a-mês as faixas + economia. Não aparece quando proposta não tem desconto.
 */
function renderCronogramaAdmin(
  b: PricingSnapshot['breakdown'] & {
    descontoEscalonado?: {
      cronograma: Array<{
        mesInicio: number;
        mesFim: number;
        valorMensal: number;
        tipo: 'pct' | 'valor' | null;
        valorAplicado: number | null;
        motivo?: string;
      }>;
      totalContratoComDesconto: number;
      totalContratoSemDesconto: number;
      economiaTotal: number;
    };
  },
): string {
  const desc = b.descontoEscalonado;
  if (!desc || desc.cronograma.length === 0) return '';

  const linhas = desc.cronograma
    .map((l) => {
      const mes =
        l.mesFim !== l.mesInicio ? `Mês ${l.mesInicio}–${l.mesFim}` : `Mês ${l.mesInicio}`;
      const tipoLabel =
        l.tipo === 'pct'
          ? `<span class="rip-cron-tag">${l.valorAplicado}% off</span>`
          : l.tipo === 'valor'
            ? `<span class="rip-cron-tag">-${fmtBRL(l.valorAplicado ?? 0)}</span>`
            : `<span class="rip-cron-tag rip-cron-cheio">valor cheio</span>`;
      return `
        <div class="rip-cron-linha">
          <div class="rip-cron-mes">${mes}</div>
          <div class="rip-cron-valor">${fmtBRL(l.valorMensal)}/mês</div>
          <div class="rip-cron-tipo">${tipoLabel}${l.motivo ? ` <em style="color:var(--color-text-muted);font-size:11px">— ${escapeHtml(l.motivo)}</em>` : ''}</div>
        </div>`;
    })
    .join('');

  return `
    <div class="rip-cronograma">
      <div class="rip-cron-titulo">🎁 Desconto promocional escalonado</div>
      <div class="rip-cron-tabela">${linhas}</div>
      <div class="rip-cron-resumo">
        <span>Economia total: <strong>${fmtBRL(desc.economiaTotal)}</strong></span>
        <span style="color:var(--color-text-muted);font-size:11px">
          Total com desconto: <strong>${fmtBRL(desc.totalContratoComDesconto)}</strong>
          (sem: <span style="text-decoration:line-through">${fmtBRL(desc.totalContratoSemDesconto)}</span>)
        </span>
      </div>
    </div>
    <style>
      .rip-cronograma { margin: 12px 0; padding: 12px 16px; background: rgba(212, 160, 23, 0.06); border-left: 3px solid #d4a017; border-radius: 4px; }
      .rip-cron-titulo { font-weight: 700; font-size: 13px; margin-bottom: 8px; }
      .rip-cron-tabela { display: flex; flex-direction: column; gap: 4px; }
      .rip-cron-linha { display: grid; grid-template-columns: 110px 140px 1fr; gap: 8px; padding: 6px 10px; background: #fff; border-radius: 3px; align-items: center; font-size: 13px; }
      .rip-cron-mes { font-weight: 600; color: #d4a017; }
      .rip-cron-valor { font-weight: 600; }
      .rip-cron-tag { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; background: #1f4e79; color: #fff; }
      .rip-cron-cheio { background: #888; }
      .rip-cron-resumo { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 8px; border-top: 1px dashed rgba(0,0,0,0.1); font-size: 12px; }
    </style>
  `;
}

function renderResumoInvestimento(snap: PricingSnapshot): string {
  // Guard: snapshots legados (clone de proposta antiga, backend não atualizado)
  // podem ter Essencial cobrando inventário. Zera defensivamente aqui.
  const ehEssencial = snap.breakdown.pacote_key === 'essencial';
  let b = snap.breakdown;
  if (ehEssencial && b.inventario.subtotal > 0) {
    const novoMensal = b.pacote.subtotal + b.addons.subtotal;
    b = {
      ...b,
      inventario: { linhas: [], subtotal: 0 },
      mensalRecorrente: novoMensal,
      mensalPrimeiros6Meses: novoMensal + b.onboarding.parcela,
    };
  }
  const renderLinha = (l: LinhaBreakdown): string => `
    <div class="rip-linha">
      <div class="rip-label">${escapeHtml(l.label)}${l.multiplicadorAplicado ? ` <span class="rip-mult">×${b.multiplicador.toFixed(2)}</span>` : ''}</div>
      <div class="rip-qtd">${l.qtd} × ${fmtBRL(l.unitario)}</div>
      <div class="rip-sub">${fmtBRL(l.subtotal)}</div>
    </div>`;
  const renderGrupo = (
    titulo: string,
    linhas: LinhaBreakdown[],
    subtotal: number,
    sublabel = 'Subtotal',
  ): string => {
    if (!linhas.length) return '';
    return `
      <div class="rip-grupo">
        <div class="rip-grupo-h">${escapeHtml(titulo)}</div>
        ${linhas.map(renderLinha).join('')}
        <div class="rip-linha rip-subtotal">
          <div class="rip-label">${escapeHtml(sublabel)}</div>
          <div></div>
          <div class="rip-sub">${fmtBRL(subtotal)}</div>
        </div>
      </div>`;
  };

  // Pacote + Dispositivos do parque + Módulos Premium mesclados num bloco só.
  // Módulos Premium aparecem como linhas extras dentro do Pacote (mesmo padrão
  // da view pública) — assim Subtotal Pacote = pacote + inventário + módulos.
  const linhasPremiumModulos: LinhaBreakdown[] = (b.premiumModulos?.linhas ?? []).map((l) => ({
    label: l.nome,
    qtd: l.horas,
    unitario: l.valorHora,
    subtotal: l.valorMensal,
    multiplicadorAplicado: false,
  }));
  const linhasComboDesc: LinhaBreakdown[] =
    b.premiumModulos?.todosMarcados && b.premiumModulos.comboDesconto > 0
      ? [
          {
            label: `Combo Módulos Premium (−${b.premiumModulos.comboPct}%)`,
            qtd: 1,
            unitario: -b.premiumModulos.comboDesconto,
            subtotal: -b.premiumModulos.comboDesconto,
            multiplicadorAplicado: false,
          },
        ]
      : [];
  const linhasPacoteCompleto = [
    ...b.pacote.linhas,
    ...b.inventario.linhas,
    ...linhasPremiumModulos,
    ...linhasComboDesc,
  ];
  const subtotalPacoteCompleto =
    b.pacote.subtotal + b.inventario.subtotal + (b.premiumModulos?.totalLiquido ?? 0);

  return `
    <section class="detalhe-acoes">
      <h3 class="detalhe-h3">Resumo de investimento</h3>
      <div class="rip-card">
        ${renderGrupo(`Pacote ${b.pacote.nome}`, linhasPacoteCompleto, subtotalPacoteCompleto, 'Subtotal pacote')}
        ${renderGrupo('Add-ons', b.addons.linhas, b.addons.subtotal, 'Subtotal add-ons')}
        ${
          b.haas && b.haas.linhas.length
            ? `
          <div class="rip-grupo" style="background:rgba(212,160,23,0.06);border-left:3px solid #d4a017;padding:8px 12px;border-radius:4px;">
            <div class="rip-grupo-h">HaaS — Hardware como Serviço (Adendo HaaS)</div>
            ${b.haas.linhas
              .map((l) => {
                const labelMap: Record<string, string> = {
                  mikrotik: 'Mikrotik',
                  mikrotikWireless: 'Mikrotik Wireless',
                  accessPoint: 'Access Point',
                  switch: 'Switch',
                  nobreak: 'Nobreak',
                };
                const tipoLabel = labelMap[l.tipo] ?? l.tipo;
                return `
                <div class="rip-linha">
                  <div class="rip-label">${escapeHtml(tipoLabel)} ${l.modelo ? `<span style="color:var(--color-text-muted);font-size:11px">— ${escapeHtml(l.modelo)}</span>` : ''}</div>
                  <div>${l.qtd} × ${fmtBRL(l.valorMensalUnit)}</div>
                  <div class="rip-sub">${fmtBRL(l.subtotalMensal)}</div>
                </div>`;
              })
              .join('')}
            <div class="rip-linha rip-subtotal">
              <div class="rip-label"><strong>Subtotal HaaS</strong></div>
              <div></div>
              <div class="rip-sub"><strong>${fmtBRL(b.haas.totalMensal)}</strong></div>
            </div>
            <div class="rip-linha" style="opacity:0.75;font-size:11px;">
              <div class="rip-label">Valor total dos equipamentos (referência cláusula 9.2)</div>
              <div></div>
              <div class="rip-sub">${fmtBRL(b.haas.totalVenda)}</div>
            </div>
          </div>`
            : ''
        }
        <div class="rip-total">
          <div class="rip-total-label">Mensal recorrente</div>
          <div class="rip-total-valor">${fmtBRL(b.mensalRecorrente)}</div>
        </div>
        ${renderCronogramaAdmin(b)}
        ${
          b.onboarding.total > 0
            ? `
          <div class="rip-onboarding">
            <div class="rip-grupo-h">
              ${b.onboarding.tipo === 'dimensional' ? 'Onboarding (dimensional)' : 'Onboarding ' + escapeHtml(b.onboarding.tipo)}
              ${b.onboarding.editadoManual ? ' <span style="color:#b45309; font-weight:600;">⚠ editado manualmente</span>' : ''}
            </div>
            ${
              b.onboarding.breakdown
                ? `
              <div class="rip-linha"><div class="rip-label">Base</div><div></div><div class="rip-sub">${fmtBRL(b.onboarding.breakdown.baseFixo)}</div></div>
              ${b.onboarding.breakdown.linhas
                .map(
                  (l) => `
                <div class="rip-linha">
                  <div class="rip-label">${escapeHtml(l.label)}</div>
                  <div>${l.qtd} × ${fmtBRL(l.unitario)}</div>
                  <div class="rip-sub">${fmtBRL(l.subtotal)}</div>
                </div>`,
                )
                .join('')}
              <div class="rip-linha">
                <div class="rip-label">Multiplicador ×${b.onboarding.breakdown.multiplicador.toFixed(2)}</div>
                <div></div>
                <div class="rip-sub">${fmtBRL(b.onboarding.total - b.onboarding.breakdown.subtotalAntesMultiplicador)}</div>
              </div>
            `
                : ''
            }
            <div class="rip-linha"><div class="rip-label"><strong>Total</strong></div><div></div><div class="rip-sub"><strong>${fmtBRL(b.onboarding.total)}</strong></div></div>
            ${
              b.onboarding.valorSugerido !== undefined &&
              b.onboarding.valorSugerido !== b.onboarding.total
                ? `
              <div class="rip-linha" style="opacity:0.7; font-size:11px;">
                <div class="rip-label">Sugerido pelo sistema</div>
                <div></div>
                <div class="rip-sub">${fmtBRL(b.onboarding.valorSugerido)}</div>
              </div>`
                : ''
            }
            <div class="rip-linha"><div class="rip-label">Parcela (6×)</div><div></div><div class="rip-sub">${fmtBRL(b.onboarding.parcela)}</div></div>
            ${
              // Quando há cronograma de desconto escalonado, ele já cobre o
              // detalhe mês-a-mês com a parcela do onboarding — esconde a linha
              // "Mensal nos 6 primeiros" pra evitar redundância/inconsistência.
              (b as { descontoEscalonado?: unknown }).descontoEscalonado
                ? ''
                : `<div class="rip-linha rip-subtotal"><div class="rip-label">Mensal nos 6 primeiros meses</div><div></div><div class="rip-sub">${fmtBRL(b.mensalPrimeiros6Meses)}</div></div>`
            }
          </div>`
            : ''
        }
      </div>
    </section>
  `;
}

function formatDoc(c: { cnpj?: string; cpf?: string }): string | null {
  if (c.cnpj && c.cnpj.length === 14) {
    return `${c.cnpj.slice(0, 2)}.${c.cnpj.slice(2, 5)}.${c.cnpj.slice(5, 8)}/${c.cnpj.slice(8, 12)}-${c.cnpj.slice(12)}`;
  }
  if (c.cpf && c.cpf.length === 11) {
    return `${c.cpf.slice(0, 3)}.${c.cpf.slice(3, 6)}.${c.cpf.slice(6, 9)}-${c.cpf.slice(9)}`;
  }
  return null;
}

function formatTelefone(t: string): string {
  if (t.length === 11) return `(${t.slice(0, 2)}) ${t.slice(2, 7)}-${t.slice(7)}`;
  if (t.length === 10) return `(${t.slice(0, 2)}) ${t.slice(2, 6)}-${t.slice(6)}`;
  return t;
}

/**
 * Renderiza a seção "Observações" no detalhe admin.
 * O campo `observacoes` da proposta contém markdown estruturado com várias
 * seções (Problemas, Segurança, Inventário, Add-ons, Observações livres) —
 * mas no detalhe admin só queremos mostrar o trecho de Observações livres
 * pra evitar duplicação com o resumo do contrato.
 *
 * Se o markdown não tem `## Observações`, mostra o conteúdo bruto (compat
 * com propostas antigas que tinham texto livre direto).
 */
function renderObservacoesEstruturadas(md: string): string {
  const partes = md.split(/\n## /).map((s, i) => (i === 0 ? s.replace(/^## /, '') : s));
  const mapa: Record<string, string> = {};
  for (const sec of partes) {
    const [header, ...body] = sec.split('\n');
    if (!header) continue;
    mapa[header.trim()] = body.join('\n').trim();
  }

  const livres = (mapa['Observações'] ?? mapa['Observacoes'] ?? '').trim();

  // Se tem markdown estruturado mas sem campo livre, esconde a seção (info
  // estruturada já aparece no resumo do contrato/diagnóstico).
  const temEstrutura =
    mapa['Problemas atuais'] ||
    mapa['Segurança & Backup'] ||
    mapa['Seguranca e Backup'] ||
    mapa['Inventário do parque'] ||
    mapa['Inventario do parque'] ||
    mapa['Add-ons mensais'];

  // Defesa: se livres está vazio OU contém markers de outra seção (md mal
  // estruturado herdado), esconde.
  if (!livres || livres.includes('## ')) {
    if (temEstrutura) return '';
    // Md totalmente sem estrutura — mostra texto cru (proposta antiga)
    return `
      <section style="grid-column: 1 / -1">
        <h3 class="detalhe-h3">Observações</h3>
        <p style="white-space: pre-wrap">${escapeHtml(md)}</p>
      </section>`;
  }

  return `
    <section style="grid-column: 1 / -1">
      <h3 class="detalhe-h3">Observações</h3>
      <p style="white-space: pre-wrap; margin: 0">${escapeHtml(livres)}</p>
    </section>`;
}

/**
 * Renderiza contatos do contrato no detalhe admin (#250).
 * Mostra assinatura, financeiro, NF/boleto e implantação (com telefone obrigatório).
 */
function renderContatosContrato(c: NonNullable<Proposta['contatosContrato']>): string {
  return `
    <section style="grid-column: 1 / -1">
      <h3 class="detalhe-h3">Contatos do contrato</h3>
      <dl class="detalhe-dl">
        <dt>Assinatura</dt><dd>${escapeHtml(c.assinaturaEmail)}</dd>
        <dt>Financeiro</dt><dd>${escapeHtml(c.financeiro.nome)} — ${escapeHtml(c.financeiro.email)}</dd>
        ${c.notaFiscalEmail ? `<dt>NF/boleto</dt><dd>${escapeHtml(c.notaFiscalEmail)}</dd>` : ''}
        ${
          c.implantacao
            ? `<dt>Implantação</dt><dd>${escapeHtml(c.implantacao.nome)} — ${escapeHtml(c.implantacao.email)}${c.implantacao.telefone ? ` · ${escapeHtml(c.implantacao.telefone)}` : ''}</dd>`
            : ''
        }
      </dl>
    </section>
  `;
}

/**
 * Renderiza dados que o cliente preencheu pro contrato (#256).
 * Inclui dia de vencimento, razão social, endereço, responsável legal.
 * Consulta rápida no admin sem precisar abrir o PDF do contrato.
 */
function renderDadosContratoAdmin(d: NonNullable<Proposta['dadosContrato']>): string {
  const r = d.responsavelLegal;
  const e = d.endereco;
  const endereco = `${escapeHtml(e.rua)}, ${escapeHtml(e.numero)}${
    e.complemento ? ' - ' + escapeHtml(e.complemento) : ''
  } — ${escapeHtml(e.bairro)}, ${escapeHtml(e.cidade)}/${escapeHtml(e.uf)} · CEP ${escapeHtml(e.cep)}`;
  const doc = d.cnpj
    ? `CNPJ ${escapeHtml(d.cnpj)}`
    : d.cpf
      ? `CPF ${escapeHtml(d.cpf)}`
      : 'não informado';
  return `
    <section style="grid-column: 1 / -1; padding: var(--sp-3) var(--sp-4); background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px; margin-top: var(--sp-2)">
      <h3 class="detalhe-h3" style="color:#166534;margin:0 0 var(--sp-3)">
        📋 Dados do contrato (preenchidos pelo cliente)
      </h3>
      <dl class="detalhe-dl">
        <dt>Razão social</dt><dd>${escapeHtml(d.razaoSocial)}</dd>
        <dt>Documento</dt><dd>${doc}</dd>
        ${d.inscricaoEstadual ? `<dt>IE</dt><dd>${escapeHtml(d.inscricaoEstadual)}</dd>` : ''}
        ${d.inscricaoMunicipal ? `<dt>IM</dt><dd>${escapeHtml(d.inscricaoMunicipal)}</dd>` : ''}
        <dt>Endereço</dt><dd>${endereco}</dd>
        <dt>Dia de vencimento</dt><dd><strong style="color:#166534">Dia ${d.diaVencimento} de cada mês</strong></dd>
        <dt>Responsável legal</dt>
        <dd>
          ${escapeHtml(r.nome)} — ${escapeHtml(r.cargo)}<br/>
          CPF ${escapeHtml(r.cpf)} · ${escapeHtml(r.email)} · ${escapeHtml(r.telefone)}
        </dd>
        <dt>Preenchido em</dt><dd>${escapeHtml(new Date(d.preenchidoEm).toLocaleString('pt-BR'))}</dd>
      </dl>
    </section>
  `;
}

/**
 * Renderiza observações INTERNAS no detalhe admin (#237).
 * Bloco visualmente destacado (cor de alerta) com aviso de que NÃO é enviado ao cliente.
 */
function renderObservacoesInternas(texto: string): string {
  const t = texto.trim();
  if (!t) return '';
  return `
    <section style="grid-column: 1 / -1; padding: var(--sp-3) var(--sp-4); background: #fffaf0; border: 2px dashed #d97706; border-radius: 6px; margin-top: var(--sp-2)">
      <h3 class="detalhe-h3" style="display:flex;align-items:center;gap:6px;color:#92400e;margin:0 0 var(--sp-2)">
        📝 Observações internas
        <span style="font-size:var(--fs-xs);font-weight:400;color:#92400e">— não enviadas ao cliente</span>
      </h3>
      <p style="white-space: pre-wrap; margin: 0; color: #5a3a0a">${escapeHtml(t)}</p>
    </section>`;
}

let stylesInjected = false;
function injectDetalheStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    .detalhe-header {
      display: flex; justify-content: space-between; align-items: flex-start;
      gap: var(--sp-4); margin-bottom: var(--sp-6);
      border-bottom: 1px solid var(--color-border); padding-bottom: var(--sp-4);
    }
    .detalhe-titulo {
      font-family: var(--font-display); font-size: var(--fs-3xl);
      margin: 0; color: var(--color-text);
    }
    .detalhe-subtitulo { color: var(--color-text-secondary); margin-top: var(--sp-1); }
    .badge-status { font-size: var(--fs-sm); padding: var(--sp-2) var(--sp-3); }
    .detalhe-grid {
      display: grid; grid-template-columns: 1fr; gap: var(--sp-6);
      margin-bottom: var(--sp-6);
    }
    @media (min-width: 700px) { .detalhe-grid { grid-template-columns: 1fr 1fr; } }
    .detalhe-h3 {
      font-family: var(--font-display); font-size: var(--fs-lg);
      margin: 0 0 var(--sp-3); color: var(--color-text-secondary);
      text-transform: uppercase; letter-spacing: 0.5px;
    }
    .detalhe-dl {
      display: grid; grid-template-columns: max-content 1fr; gap: var(--sp-2) var(--sp-4);
      margin: 0;
    }
    .detalhe-dl dt { color: var(--color-text-muted); font-size: var(--fs-sm); }
    .detalhe-dl dd { margin: 0; }
    .detalhe-acoes {
      border-top: 1px solid var(--color-border);
      padding-top: var(--sp-5);
      margin-top: var(--sp-6);
    }
    .acoes-status {
      display: grid;
      grid-template-columns: minmax(180px, 220px) minmax(220px, 1fr) auto;
      gap: var(--sp-3);
      align-items: end;
      background: var(--color-surface-alt);
      padding: var(--sp-4);
      border-radius: 6px;
      border: 1px solid var(--color-border);
    }
    @media (max-width: 640px) {
      .acoes-status { grid-template-columns: 1fr; }
      .acoes-status > .btn { justify-self: start; }
    }
    .link-publico-box {
      background: var(--color-surface-alt);
      padding: var(--sp-4);
      border-radius: 6px;
      border: 1px solid var(--color-border);
      display: flex;
      gap: var(--sp-3);
      flex-wrap: wrap;
      align-items: center;
    }
    .detalhe-timeline {
      margin: 0 0 var(--sp-6);
      padding: var(--sp-5) var(--sp-4);
      background: var(--color-surface-alt);
      border: 1px solid var(--color-border);
      border-radius: 8px;
    }
    .timeline-loading {
      font-size: var(--fs-sm);
      color: var(--color-text-muted);
      text-align: center;
    }
    .tl-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      grid-template-columns: repeat(8, minmax(0, 1fr));
      gap: 0;
      position: relative;
    }
    .tl-step {
      position: relative;
      text-align: center;
      padding: var(--sp-2) var(--sp-1) 0;
    }
    .tl-step + .tl-step::before {
      content: '';
      position: absolute;
      top: 18px;
      left: -50%;
      right: 50%;
      height: 2px;
      background: var(--color-border-strong);
      z-index: 0;
    }
    .tl-completa + .tl-completa::before,
    .tl-atual::before {
      background: var(--color-primary);
    }
    .tl-dot {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: var(--color-surface);
      border: 2px solid var(--color-border-strong);
      color: var(--color-text-muted);
      font-size: 14px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto var(--sp-2);
      position: relative;
      z-index: 1;
    }
    .tl-completa .tl-dot {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: #fff;
    }
    .tl-atual .tl-dot {
      border-color: var(--color-primary);
      color: var(--color-primary);
      box-shadow: 0 0 0 4px var(--color-primary-light);
    }
    .tl-label {
      font-size: var(--fs-xs);
      font-weight: 600;
      color: var(--color-text);
      line-height: 1.2;
      margin-bottom: 2px;
    }
    .tl-futura .tl-label { color: var(--color-text-muted); font-weight: 500; }
    .tl-data {
      font-size: 10px;
      color: var(--color-text-muted);
      line-height: 1.2;
      min-height: 12px;
    }
    .tl-terminal {
      margin-top: var(--sp-4);
      padding: var(--sp-3);
      border-radius: 6px;
      font-size: var(--fs-sm);
      text-align: center;
    }
    .tl-terminal-perdida,
    .tl-terminal-cancelada {
      background: var(--color-danger-bg);
      color: var(--color-danger);
    }
    @media (max-width: 700px) {
      .tl-list { grid-template-columns: repeat(4, 1fr); row-gap: var(--sp-4); }
      .tl-step + .tl-step::before { display: none; }
    }
    .detalhe-json {
      background: var(--color-surface-alt); padding: var(--sp-3); border-radius: 4px;
      overflow: auto; font-size: var(--fs-xs); margin-top: var(--sp-2);
    }
    .obs-grid {
      display: grid; grid-template-columns: 1fr; gap: var(--sp-3); margin-top: var(--sp-2);
    }
    @media (min-width: 700px) { .obs-grid { grid-template-columns: 1fr 1fr; } }
    .obs-bloco {
      padding: var(--sp-3) var(--sp-4); border-radius: 8px; border-left: 4px solid;
      background: var(--color-surface-alt, #fafafa);
    }
    .obs-vermelho { border-left-color: #dc2626; background: #fef2f2; }
    .obs-laranja { border-left-color: #ea580c; background: #fff7ed; }
    .obs-azul    { border-left-color: #2563eb; background: #eff6ff; }
    .obs-cinza   { border-left-color: #525252; background: #f5f5f5; }
    .obs-livre   { border-left-color: var(--color-primary, #d4a017); background: #fffbeb; }
    .obs-titulo {
      font-family: var(--font-display); font-size: var(--fs-sm); font-weight: 700;
      color: var(--color-text); margin-bottom: var(--sp-2);
      letter-spacing: 0.3px;
    }
    .obs-lista { margin: 0; padding-left: var(--sp-4); font-size: var(--fs-sm); line-height: 1.55; color: var(--color-text); }
    .obs-lista li { margin-bottom: 4px; }
    /* ── Resumo de investimento (rip-*) ──────────────────────────────────
       Bloco compacto com 3 colunas: descrição | qtd×unit | subtotal.
       Grupos visuais separados (Pacote, Add-ons, Onboarding) com header
       em maiúsculas, e bloco escuro destacado pro Mensal Recorrente. */
    .rip-card {
      background: var(--color-surface, #fff);
      border: 1px solid var(--color-border);
      border-radius: 8px;
      padding: var(--sp-4) var(--sp-5);
      margin-top: var(--sp-2);
    }
    .rip-grupo {
      padding-bottom: var(--sp-3);
      margin-bottom: var(--sp-3);
      border-bottom: 1px solid var(--color-border);
    }
    .rip-grupo:last-of-type { border-bottom: 0; margin-bottom: 0; padding-bottom: 0; }
    .rip-grupo-h {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: var(--color-primary, #d4a017);
      margin: 0 0 var(--sp-2);
    }
    /* Linha tripla: label | qtd×unit | subtotal */
    .rip-linha {
      display: grid;
      grid-template-columns: 1fr auto 110px;
      gap: var(--sp-3);
      align-items: baseline;
      padding: 4px 0;
      font-size: var(--fs-sm);
    }
    .rip-label {
      color: var(--color-text);
      min-width: 0;
    }
    .rip-qtd {
      color: var(--color-text-muted, #888);
      font-size: var(--fs-xs);
      font-family: ui-monospace, monospace;
      text-align: right;
      white-space: nowrap;
    }
    .rip-sub {
      font-family: ui-monospace, monospace;
      font-weight: 600;
      color: var(--color-text);
      text-align: right;
      white-space: nowrap;
    }
    .rip-mult {
      color: var(--color-text-muted);
      font-size: 10px;
      font-weight: 400;
      letter-spacing: 0.5px;
    }
    .rip-subtotal {
      border-top: 1px dashed var(--color-border);
      margin-top: 4px;
      padding-top: 6px;
    }
    .rip-subtotal .rip-label {
      font-weight: 700;
      color: var(--color-text-secondary, #555);
      text-transform: uppercase;
      font-size: var(--fs-xs);
      letter-spacing: 0.5px;
    }
    /* Bloco preto destacado: Mensal recorrente */
    .rip-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #1a1a1a;
      color: #fff;
      padding: var(--sp-3) var(--sp-4);
      border-radius: 6px;
      margin: var(--sp-3) 0;
    }
    .rip-total-label {
      font-size: var(--fs-xs);
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: var(--color-primary, #d4a017);
    }
    .rip-total-valor {
      font-family: ui-monospace, monospace;
      font-size: var(--fs-2xl, 22px);
      font-weight: 700;
    }
    /* Onboarding: bloco com fundo leve dourado pra diferenciar */
    .rip-onboarding {
      background: #fff8e6;
      border-left: 3px solid var(--color-primary, #d4a017);
      padding: var(--sp-3) var(--sp-4);
      border-radius: 0 6px 6px 0;
      margin-top: var(--sp-3);
    }
    .rip-onboarding .rip-grupo-h { margin-bottom: var(--sp-2); }
    .rip-onboarding .rip-linha { font-size: var(--fs-xs); padding: 3px 0; }
    .rip-onboarding .rip-subtotal { margin-top: 6px; padding-top: 6px; }
    .rip-onboarding .rip-subtotal .rip-sub { font-size: var(--fs-sm); }
  `;
  document.head.appendChild(styleEl);
}
